import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@mineoptics/brand/logo";
import { PillLink } from "./PillLink";
import { getLenis } from "@/lib/scroll";
import { DOCS_URL, LOGIN_URL, SIGNUP_URL, isExternal } from "@/lib/site";

type Tone = "dark" | "light";

const LINKS = [
  { label: "Platform", to: "/#platform" },
  { label: "Drill & blast", to: "/features/drill-blast" },
  { label: "Pricing", to: "/pricing" },
  { label: "Docs", to: DOCS_URL },
];

/** Height of the strip under the bar that decides which palette the bar uses. */
const PROBE_Y = 40;

function NavLink({ to, label, className, onClick }: { to: string; label: string; className: string; onClick?: () => void }) {
  const inner = (
    <span className="roll">
      <span data-text={label}>{label}</span>
    </span>
  );
  return isExternal(to) ? (
    <a href={to} className={className} onClick={onClick}>
      {inner}
    </a>
  ) : (
    <Link to={to} className={className} onClick={onClick}>
      {inner}
    </Link>
  );
}

export function Nav() {
  const { pathname } = useLocation();
  const [tone, setTone] = useState<Tone>("dark");
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);

  // Follow the palette of whichever section is under the bar, and get out of
  // the way while the visitor scrolls down.
  useEffect(() => {
    let frame = 0;
    let lastY = window.scrollY;

    const update = () => {
      frame = 0;
      const y = window.scrollY;

      let next: Tone = "dark";
      document.querySelectorAll<HTMLElement>("main section[data-theme], footer[data-theme]").forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= PROBE_Y && rect.bottom > PROBE_Y) next = section.dataset.theme === "light" ? "light" : "dark";
      });
      setTone(next);
      setScrolled(y > 24);

      if (Math.abs(y - lastY) > 8) {
        setHidden(y > lastY && y > 480);
        lastY = y;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    schedule();
    const settle = window.setTimeout(schedule, 300);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [pathname]);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    getLenis()?.stop();
    document.documentElement.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      getLenis()?.start();
      document.documentElement.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const barTone: Tone = open ? "dark" : tone;

  return (
    <>
      <header
        data-theme={barTone}
        className={`fixed inset-x-0 top-0 z-50 transition-transform duration-500 ease-out-expo ${
          hidden && !open ? "-translate-y-full" : "translate-y-0"
        }`}
      >
        <div className="shell pt-3">
          <div
            className={`flex h-14 items-center justify-between rounded-full border pl-5 pr-2 transition-[background-color,border-color,backdrop-filter] duration-500 ${
              scrolled && !open ? "border-fg/10 bg-bg/70 backdrop-blur-xl" : "border-transparent bg-transparent"
            }`}
          >
            <Link to="/" aria-label="MineOptics home" className="shrink-0">
              <Logo />
            </Link>

            <nav aria-label="Primary" className="hidden items-center gap-9 md:flex">
              {LINKS.map((link) => (
                <NavLink
                  key={link.label}
                  {...link}
                  className="text-[0.9375rem] text-fg/75 transition-colors duration-300 hover:text-fg"
                />
              ))}
            </nav>

            <div className="flex items-center gap-1.5">
              <a
                href={LOGIN_URL}
                className="hidden px-4 text-[0.9375rem] text-fg/75 transition-colors duration-300 hover:text-fg sm:block"
              >
                Sign in
              </a>
              <PillLink to={SIGNUP_URL} label="Start free" size="sm" className="hidden sm:inline-flex" />
              <button
                type="button"
                aria-expanded={open}
                aria-controls="mobile-menu"
                aria-label={open ? "Close menu" : "Open menu"}
                onClick={() => setOpen((value) => !value)}
                className="relative ml-1 flex h-10 w-10 items-center justify-center rounded-full border border-fg/20 md:hidden"
              >
                <span
                  className={`absolute h-px w-4 bg-fg transition-transform duration-500 ease-out-expo ${
                    open ? "rotate-45" : "-translate-y-[3px]"
                  }`}
                />
                <span
                  className={`absolute h-px w-4 bg-fg transition-transform duration-500 ease-out-expo ${
                    open ? "-rotate-45" : "translate-y-[3px]"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div
        id="mobile-menu"
        data-theme="dark"
        aria-hidden={!open}
        className={`fixed inset-0 z-40 flex flex-col bg-bg px-5 pb-8 pt-28 transition-[opacity,visibility] duration-500 md:hidden ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <nav aria-label="Mobile" className="flex flex-1 flex-col">
          {LINKS.map((link, index) => (
            <NavLink
              key={link.label}
              {...link}
              onClick={() => setOpen(false)}
              className={`title border-b border-fg/15 py-5 !text-[2.5rem] transition-[transform,opacity] duration-700 ease-out-expo ${
                open ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
              } ${["delay-75", "delay-100", "delay-150", "delay-200"][index]}`}
            />
          ))}
        </nav>
        <div className="flex flex-col gap-3">
          <PillLink to={SIGNUP_URL} label="Start free" variant="accent" arrow />
          <PillLink to={LOGIN_URL} label="Sign in" variant="outline" />
        </div>
      </div>
    </>
  );
}
